/**
 * Display formatters shared by the screens.
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable.
 */

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/** Shown for a missing value instead of a misleading 0 or today's date. */
export const EMPTY_VALUE = "—";

/** Shown on a receipt when a bill has no (valid) payment date. */
export const NOT_PAID = "Not paid";

// A year before 1900 is a default placeholder (0001-01-01 from .NET), not a real date — the same
// rule billHelpers.meaningfulDate uses — so it reads as missing rather than "Jan 1, 1" (AB#6797).
const toValidDate = (d?: string | Date | null): Date | null => {
  if (d === null || d === undefined) return null;
  if (typeof d === "string" && d.trim() === "") return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < 1900) return null;
  return date;
};

/** US dollars, e.g. 1234.5 -> "$1,234.50". Missing or non-numeric -> "—". */
export function formatMoney(n?: number | null): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return EMPTY_VALUE;
  return usd.format(n);
}

/**
 * Receipt payment date, e.g. "Oct 1, 2026, 9:42 AM". Returns "Not paid" when the date
 * is null, undefined, empty or unparsable — it never falls back to the current time.
 * Pass `timeZone` (IANA name) to pin the zone; the default is the browser's.
 */
export function formatPaymentDate(
  d?: string | Date | null,
  opts?: { timeZone?: string }
): string {
  const date = toValidDate(d);
  if (!date) return NOT_PAID;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: opts?.timeZone,
  }).format(date);
}

/**
 * Receipt payment date for a bill: "Not paid" for an unpaid bill (a stored date on it is stale),
 * the date for a paid one, and "—" for a paid bill with no date — a Paid ACH bill whose date has
 * not reached CSIPay yet is paid, so "Not paid" would be wrong for it (AB#6797).
 */
export function formatPaidDate(
  paid: boolean,
  d?: string | Date | null,
  opts?: { timeZone?: string }
): string {
  if (!paid) return NOT_PAID;
  return toValidDate(d) ? formatPaymentDate(d, opts) : EMPTY_VALUE;
}

/** Calendar date as MM/dd/yyyy in the browser's zone. Missing or invalid -> "—". */
export function formatDate(d?: string | Date | null): string {
  const date = toValidDate(d);
  if (!date) return EMPTY_VALUE;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getFullYear()}`;
}
